import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state';
import { Mark, mergeAttributes } from '@tiptap/core';
import type { LiveblocksThread } from 'providers/CommentsContext';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    commentThread: {
      addThread: (thread: LiveblocksThread) => ReturnType;
      removeThread: (threadId: string) => ReturnType;
      highlightThread: (threadId: string) => ReturnType;
      hasThread: (threadId: string) => ReturnType;
      clearHighlight: () => ReturnType;
      setVisibleThreads: (threadIds: string[]) => ReturnType;
      getThreadCountAtSelection: () => ReturnType;
      getGroupedThreadPositions: () => ReturnType;
      resolveThread: (threadId: string) => ReturnType;
      unresolveThread: (threadId: string) => ReturnType;
    };
  }
}

export const Comments = Mark.create({
  name: 'commentThread',

  addOptions() {
    return {
      enabled: true,
    };
  },

  inclusive: false,
  excludes: '',

  addStorage() {
    return {
      onThreadClick: null,
      visibleThreadIds: new Set(),
      lastFoundThreadPosition: null as number | null,
      threadCountAtSelection: 0,
      groupedThreadPositions: [] as Array<{
        position: number;
        threads: Array<{
          threadId: string;
          isResolved: boolean;
        }>;
      }>,
    };
  },

  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-thread-id'),
        renderHTML: (attributes) => {
          if (!attributes.threadId) {
            return {};
          }
          return {
            'data-thread-id': attributes.threadId,
          };
        },
      },
      isResolved: {
        default: false,
        parseHTML: (element) => element.classList.contains('resolved'),
        renderHTML: (attrs) => {
          if (!attrs.isResolved) {
            return null;
          }
          return {
            class: 'resolved',
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-thread-id]',
      },
    ];
  },

  renderHTML({ HTMLAttributes, mark }) {
    const threadId = mark.attrs.threadId;
    const storage = this.storage;

    const isVisible = !threadId || !storage.visibleThreadIds || storage.visibleThreadIds.has(threadId);

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: `comment-thread-mark ${!isVisible ? 'comment-thread-disabled' : ''}`,
        'data-thread-id': threadId,
      }),
      0,
    ];
  },

  addCommands() {
    return {
      addThread: (thread: LiveblocksThread) => {
        return ({ tr, state }) => {
          if (!this.options.enabled) {
            return false;
          }

          if (!thread.id) {
            return false;
          }

          const { selection } = state;
          let { from, to } = selection;

          if (selection instanceof NodeSelection) {
            from = selection.from;
            to = selection.to;
          } else if (from === to) {
            return false;
          }

          let hasThisThread = false;
          state.doc.nodesBetween(from, to, (node) => {
            if (node.marks) {
              const threadMarks = node.marks.filter((mark) => mark.type.name === this.name);
              if (threadMarks.some((mark) => mark.attrs.threadId === thread.id)) {
                hasThisThread = true;
                return false;
              }
            }
          });

          if (hasThisThread) {
            return false;
          }

          const mark = state.schema.marks[this.name].create({
            threadId: thread.id,
            isResolved: thread.resolved,
          });

          // Apply the mark to all text and inline nodes in the selection
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.isText) {
              const markFrom = Math.max(pos, from);
              const markTo = Math.min(pos + node.nodeSize, to);
              tr.addMark(markFrom, markTo, mark);
            } else if (node.isInline && node.type.spec.marks !== '') {
              const existingMarks = node.marks || [];
              const updatedMarks = [...existingMarks, mark];
              const newNode = node.type.create(node.attrs, node.content, updatedMarks);
              tr.replaceWith(pos, pos + node.nodeSize, newNode);
            }
          });

          return true;
        };
      },
      removeThread: (threadId: string) => {
        return ({ state, tr }) => {
          if (!threadId) {
            return false;
          }

          const { doc } = state;
          let found = false;

          doc.descendants((node, pos) => {
            if (!node.marks) return;

            const threadMark = node.marks.find((mark) => mark.type.name === this.name && mark.attrs.threadId === threadId);

            if (threadMark) {
              const filteredMarks = node.marks.filter((mark) => !(mark.type.name === this.name && mark.attrs.threadId === threadId));

              let newNode;
              if (node.isText) {
                newNode = state.schema.text(node.text || '', filteredMarks);
              } else {
                newNode = node.type.create(node.attrs, node.content, filteredMarks);
              }
              tr.replaceWith(pos, pos + node.nodeSize, newNode);
              found = true;
            }
          });

          return found;
        };
      },
      hasThread: (threadId: string) => {
        return ({ editor }) => {
          if (!threadId) {
            return false;
          }

          let hasThread = false;

          const commentElements = editor.view.dom.querySelectorAll('.comment-thread-mark');
          commentElements.forEach((element) => {
            const elementThreadId = element.getAttribute('data-thread-id');
            if (elementThreadId === threadId) {
              hasThread = true;
            }
          });

          return hasThread;
        };
      },
      highlightThread: (threadId: string) => {
        return ({ editor }) => {
          if (!threadId) {
            return false;
          }

          const commentElements = editor.view.dom.querySelectorAll('.comment-thread-mark');
          commentElements.forEach((element) => {
            const elementThreadId = element.getAttribute('data-thread-id');
            if (elementThreadId === threadId) {
              element.classList.add('comment-thread-highlighted');
            }
          });

          return true;
        };
      },
      clearHighlight: () => {
        return ({ editor }) => {
          const commentElements = editor.view.dom.querySelectorAll('.comment-thread-mark');
          commentElements.forEach((element) => {
            element.classList.remove('comment-thread-highlighted');
          });
          return true;
        };
      },
      setVisibleThreads: (threadIds: string[]) => {
        return ({ editor }) => {
          const storage = editor.storage.commentThread;
          if (storage) {
            storage.visibleThreadIds = new Set(threadIds);

            const commentElements = editor.view.dom.querySelectorAll('.comment-thread-mark');
            commentElements.forEach((element) => {
              const threadId = element.getAttribute('data-thread-id');
              if (threadId) {
                if (storage.visibleThreadIds.has(threadId)) {
                  element.classList.remove('comment-thread-disabled');
                } else {
                  element.classList.add('comment-thread-disabled');
                }
              }
            });
          }
          return true;
        };
      },
      getThreadCountAtSelection: () => {
        return ({ state, editor }) => {
          const { selection } = state;
          const { from, to } = selection;

          if (from === to) {
            return false;
          }

          const existingThreads = new Set<string>();
          state.doc.nodesBetween(from, to, (node) => {
            if (node.marks) {
              node.marks.forEach((mark) => {
                if (mark.type.name === this.name && mark.attrs.threadId && !mark.attrs.isResolved) {
                  existingThreads.add(mark.attrs.threadId);
                }
              });
            }
          });

          if (editor.storage.commentThread) {
            editor.storage.commentThread.threadCountAtSelection = existingThreads.size;
          }

          return true;
        };
      },
      getGroupedThreadPositions: () => {
        return ({ state, editor }) => {
          const { doc } = state;
          const groupedPositions: Array<{
            position: number;
            threads: Array<{
              threadId: string;
              isResolved: boolean;
            }>;
          }> = [];

          doc.descendants((node, pos) => {
            if (!node.marks) return;

            const threadMarks = node.marks.filter((mark) => mark.type.name === this.name && mark.attrs.threadId);

            if (threadMarks.length > 0) {
              const threads = threadMarks.map((mark) => ({
                threadId: mark.attrs.threadId,
                isResolved: mark.attrs.isResolved || false,
              }));

              groupedPositions.push({
                position: pos,
                threads,
              });
            }
          });

          // Store the result in editor storage
          if (editor.storage.commentThread) {
            editor.storage.commentThread.groupedThreadPositions = groupedPositions;
          }

          return true;
        };
      },
      resolveThread: (threadId: string) => {
        return ({ state, tr }) => {
          if (!threadId) {
            return false;
          }

          const { doc } = state;
          let found = false;

          doc.descendants((node, pos) => {
            if (!node.marks) return;

            const threadMark = node.marks.find((mark) => mark.type.name === this.name && mark.attrs.threadId === threadId);

            if (threadMark && !threadMark.attrs.isResolved) {
              const resolvedMark = state.schema.marks[this.name].create({
                threadId: threadId,
                isResolved: true,
              });

              const filteredMarks = node.marks.filter((mark) => !(mark.type.name === this.name && mark.attrs.threadId === threadId));
              const allMarks = [...filteredMarks, resolvedMark];

              if (node.isText) {
                const newNode = state.schema.text(node.text || '', allMarks);
                tr.replaceWith(pos, pos + node.nodeSize, newNode);
                found = true;
              } else if (node.isInline) {
                const newNode = node.type.create(node.attrs, node.content, allMarks);
                tr.replaceWith(pos, pos + node.nodeSize, newNode);
                found = true;
              }
            }
          });

          return found;
        };
      },
      unresolveThread: (threadId: string) => {
        return ({ state, tr }) => {
          if (!threadId) {
            return false;
          }

          const { doc } = state;
          let found = false;

          doc.descendants((node, pos) => {
            if (!node.marks) return;

            const threadMark = node.marks.find((mark) => mark.type.name === this.name && mark.attrs.threadId === threadId);

            if (threadMark && threadMark.attrs.isResolved) {
              const unresolvedMark = state.schema.marks[this.name].create({
                threadId: threadId,
                isResolved: false,
              });

              const filteredMarks = node.marks.filter((mark) => !(mark.type.name === this.name && mark.attrs.threadId === threadId));
              const allMarks = [...filteredMarks, unresolvedMark];

              if (node.isText) {
                const newNode = state.schema.text(node.text || '', allMarks);
                tr.replaceWith(pos, pos + node.nodeSize, newNode);
                found = true;
              } else if (node.isInline) {
                const newNode = node.type.create(node.attrs, node.content, allMarks);
                tr.replaceWith(pos, pos + node.nodeSize, newNode);
                found = true;
              }
            }
          });

          return found;
        };
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('commentThreads'),
        props: {
          handleDOMEvents: {
            click: (view, event) => {
              if (!this.options.enabled) {
                return false;
              }

              const target = event.target as HTMLElement;
              const threadElement = target.closest('.comment-thread-mark');

              if (!threadElement) {
                return false;
              }

              if (threadElement.classList.contains('comment-thread-disabled')) {
                return false;
              }

              const threadId = threadElement.getAttribute('data-thread-id');
              const onThreadClick = this.editor.storage.commentThread?.onThreadClick;

              if (threadId && onThreadClick && this.editor.storage.commentThread?.visibleThreadIds?.has(threadId)) {
                onThreadClick(threadId);
                return true;
              }

              return false;
            },
          },
        },
      }),
    ];
  },
});
